import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { getAuthToken, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { createQueueArrivalGuard } from "@/lib/queue-arrival";
import { claimAlert, emptyNotifications, type LiveNotifications } from "@/lib/live-chat-notifications";

export function useLiveChatNotifications(enabled: boolean) {
  const { user, organization } = useAuth();
  const { toast } = useToast();
  const orgId = organization?.id;
  const userId = user?.id;
  const scope = `${orgId}:${userId}`;
  const key = ["/api/live-chat/notifications", orgId, userId];
  const authorized = useRef(false);
  const revoked = useRef(false);
  const activeSocket = useRef<WebSocket>();
  const operatorOnline = useRef(false);
  const result = useQuery<LiveNotifications>({
    queryKey: key,
    enabled: enabled && !!orgId && !!userId,
    queryFn: async ({ signal }) => {
      const res = await fetch("/api/live-chat/notifications", {
        headers: { Authorization: `Bearer ${getAuthToken() || ""}` }, signal,
      });
      if (res.status === 401 || res.status === 403) {
        authorized.current = false;
        revoked.current = true;
        activeSocket.current?.close();
        queryClient.setQueryData(key, emptyNotifications);
        throw new Error("Live chat notifications are not available for this account.");
      }
      if (!res.ok) throw new Error("Could not load live chat requests.");
      authorized.current = true;
      return res.json();
    },
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    authorized.current = false;
    revoked.current = false;
    operatorOnline.current = false;
    if (!enabled || !orgId || !userId) return;
    let stopped = false;
    let socket: WebSocket | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const accept = createQueueArrivalGuard(orgId);
    const refresh = () => {
      if (stopped) return;
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/offline-notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/supervisor/sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/supervisor/stats"] });
    };
    const checkOnline = async () => {
      try {
        const res = await fetch("/api/live-chat/operator-status", { headers: { Authorization: `Bearer ${getAuthToken() || ""}` } });
        const data = res.ok ? await res.json() : null;
        if (!stopped) operatorOnline.current = data?.status === "online";
      } catch { if (!stopped) operatorOnline.current = false; }
    };
    const connect = () => {
      if (stopped || revoked.current || !getAuthToken()) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(`${proto}://${location.host}/ws/live-chat?topic=${encodeURIComponent(`queue:${orgId}`)}&access_token=${encodeURIComponent(getAuthToken()!)}`);
      activeSocket.current = socket;
      socket.onopen = () => { refresh(); void checkOnline(); };
      socket.onmessage = async ({ data }) => {
        if (stopped) return;
        try {
          const envelope = JSON.parse(data);
          if (envelope.topic !== `queue:${orgId}`) return;
          refresh();
          // Only live session_new hints can alert. REST snapshots never alert.
          if (!accept(envelope.event, envelope.payload, operatorOnline.current)) return;
          await checkOnline();
          // Revalidate permission and queued state before displaying any hint.
          const current = await queryClient.fetchQuery<LiveNotifications>({ queryKey: key, staleTime: 0 });
          if (stopped || !authorized.current || !operatorOnline.current
              || !current.items.some(x => x.sessionId === envelope.payload.id && x.unread)) return;
          // Serialize across tabs. Without Web Locks we keep the bell but skip
          // optional alerts instead of racing a localStorage check-and-set.
          if (!navigator.locks) return;
          await navigator.locks.request(`live-chat-alert:${scope}`, () => {
            if (stopped || !authorized.current || !claimAlert(localStorage, scope, envelope.payload.id, Date.now())) return;
            toast({ title: "New live-chat request", description: "A new request is waiting in Live Chat.", duration: 8000 });
            try {
              if (typeof Notification !== "undefined" && Notification.permission === "granted")
                new Notification("New live-chat request", { body: "A new request is waiting in Live Chat." });
            } catch { /* Optional OS notifications must never affect the bell. */ }
          });
        } catch { /* REST polling remains authoritative. */ }
      };
      socket.onclose = () => { if (!stopped && !revoked.current) retry = setTimeout(connect, 2000); };
      socket.onerror = () => socket?.close();
    };
    const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(`live-chat-read:${scope}`) : null;
    if (channel) channel.onmessage = refresh;
    const visibility = () => { if (document.visibilityState === "visible") { refresh(); void checkOnline(); } };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("online", refresh);
    connect();
    return () => {
      stopped = true; authorized.current = false;
      clearTimeout(retry); socket?.close(); channel?.close();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("online", refresh);
      void queryClient.cancelQueries({ queryKey: key });
      queryClient.removeQueries({ queryKey: key });
    };
    // Identity-scoped cache and connection; callbacks fetch current credentials.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, orgId, userId]);
  return result;
}
